import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import BrandHeader from "../components/BrandHeader";
import { clientLogin } from "../lib/clientAuth";
import { useClientAuth } from "../lib/clientAuthContext";

// Client sign-in for returning users. Success -> the intended destination
// (defaults to /audit).

export default function ClientLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useClientAuth();
  const dest = location.state?.from || "/audit";

  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await clientLogin({ email: form.email.trim(), password: form.password });
      refresh();
      navigate(dest, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Couldn't sign you in.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-base flex flex-col">
      <Toaster richColors position="top-center" />
      <BrandHeader small />

      <main className="flex-1 px-6 md:px-12 lg:px-16 py-10 md:py-14 max-w-md mx-auto w-full">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="label-eyebrow flex items-center gap-3">
            <span className="inline-block h-px w-10 bg-brand-primary" />
            Welcome back
          </div>
          <h1 className="font-serif text-4xl text-brand-ink leading-tight mt-4">Log in to your HER Aligned account</h1>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <label className="label-eyebrow block mb-2" htmlFor="login-email">Email</label>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                className="input-warm text-lg"
                placeholder="you@example.com"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="login-email-input"
                autoFocus
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2" htmlFor="login-pw">Password</label>
              <input
                id="login-pw"
                type="password"
                autoComplete="current-password"
                className="input-warm text-lg"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                data-testid="login-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={!form.email || !form.password || busy}
              className="btn-primary inline-flex items-center gap-2 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="login-submit-btn"
            >
              {busy ? <><Loader2 size={16} className="animate-spin" /> Signing in…</> : <>Log in <ArrowRight size={16} /></>}
            </button>
          </form>

          <div className="mt-8 text-sm text-brand-ink-muted space-y-2">
            <p>
              <Link to="/forgot-password" className="text-brand-primary font-medium hover:underline" data-testid="login-forgot-link">
                Forgot your password?
              </Link>
            </p>
            <p>
              New here?{" "}
              <Link to="/audit/signup" state={{ from: dest }} className="text-brand-primary font-medium hover:underline" data-testid="login-signup-link">
                Create an account
              </Link>
            </p>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
